import styled from 'styled-components'

/**
 * =============================================================================
 * SWEEPSTAKES MODAL STYLES
 * =============================================================================
 */
export const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  max-width: 600px;
  width: 100%;
  margin: 0 auto;
`

/* -----------------------------------------------------------------------------
 * Cabeçalho do Modal - Título e subtítulo do sorteio
 * -------------------------------------------------------------------------- */
export const ModalHeaderStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

export const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
`

export const ModalSubtitle = styled.p`
  margin: 0;
  font-size: 13px;
  color: #64748b;
`

/* -----------------------------------------------------------------------------
 * Grade de informações do sorteio (comentado no código, mas estilos mantidos)
 * -------------------------------------------------------------------------- */
export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 16px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`

export const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: #f8fafc;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 10px;
  padding: 10px 12px;
`

export const InfoLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`

export const InfoValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
`

/* -----------------------------------------------------------------------------
 * Seções do Modal - Container para "Prêmios" e "Números da sorte"
 * -------------------------------------------------------------------------- */
export const ModalSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

export const SectionLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`

export const ModalDivider = styled.div`
  height: 1px;
  width: 100%;
  background: rgba(15, 23, 42, 0.08);
`

/* =============================================================================
 * SEÇÃO DE PRÊMIOS
 * =============================================================================
 * Grid de cards de prêmios exibidos no modal
 */

export const PrizesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 14px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
`

/* -----------------------------------------------------------------------------
 * Card de Prêmio - Container clicável com número, imagem e informações
 * PrizeCard contém: PrizeNumber + PrizeImageContainer + PrizeInfo +
 * (opcional) WonPrizeBadge
 * -------------------------------------------------------------------------- */
export const PrizeCard = styled.div<{
  $active?: boolean
  $won?: boolean
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 14px;
  background: ${p =>
    p.$active ? '#fff8e1' : p.$won ? 'rgba(16, 185, 129, 0.06)' : '#f8fafc'};
  border: 2px solid
    ${p =>
      p.$active
        ? '#f5d068'
        : p.$won
        ? 'rgba(5, 150, 105, 0.2)'
        : 'rgba(15, 23, 42, 0.06)'};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.1);
    border-color: ${p =>
      p.$active
        ? '#e2b74d'
        : p.$won
        ? 'rgba(5, 150, 105, 0.35)'
        : 'rgba(15, 23, 42, 0.12)'};
  }
`

export const PrizeNumber = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${p =>
    p.$active
      ? 'linear-gradient(135deg, #f5d068 0%, #e2b74d 100%)'
      : 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)'};
  font-size: 16px;
  font-weight: 900;
  color: ${p => (p.$active ? '#2b1b0b' : '#475569')};
  box-shadow: ${p =>
    p.$active
      ? '0 4px 8px rgba(226, 183, 77, 0.3)'
      : '0 2px 4px rgba(15, 23, 42, 0.1)'};
`

export const PrizeImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 10px;
  max-width: 300px;
  max-height: 300px;
  overflow: hidden;
  background: white;
  border: 1px solid rgba(15, 23, 42, 0.06);
`

export const PrizeImage = styled.img`
  width: 100%;
  height: 100%;

  object-fit: cover;
`

/* -----------------------------------------------------------------------------
 * Prize information - Name, description and availability date
 * -------------------------------------------------------------------------- */
export const PrizeInfo = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  flex: 1;
  min-width: 100%;
`

export const PrizeName = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const PrizeDescription = styled.p`
  margin: 0;
  font-size: 12px;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`

export const PrizeAvailableAt = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  display: flex;
  align-items: center;
  gap: 4px;
`

export const PrizeValue = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #059669;
`

/* =============================================================================
 * LUCKY NUMBERS SECTION
 * =============================================================================
 * Container and grid to display the lucky numbers of the user
 */

export const SweepstakeCardLuckyNumberContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 10px;
  background-color: #f7f7f8;
  width: 100%;
  max-width: 100%;
  padding: 14px 16px;
  border-radius: 12px;
  overflow: hidden;

  @media (max-width: 480px) {
    flex-direction: row;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
  }
`

export const TicketsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  width: 100%;
  max-width: 100%;
`

/* -----------------------------------------------------------------------------
 * Lucky number card - Each number of the user with 3 variants:
 * - default: default number (yellow)
 * - winner: winner number (green)
 * - loser: loser number (gray)
 * -------------------------------------------------------------------------- */
export const SweepstakeCardLuckyNumber = styled.div<{
  $active?: boolean
  $variant?: 'default' | 'winner' | 'loser'
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 8px 14px;
  min-height: 36px;
  min-width: 64px;
  border-radius: 10px;
  font-weight: 800;
  font-size: 14px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease,
    background-color 0.2s ease;

  /* Styles by variant */
  ${p => {
    switch (p.$variant) {
      case 'winner':
        return `
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          border: 1px solid #047857;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        `
      case 'loser':
        return `
          background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%);
          color: white;
          border: 1px solid #4b5563;
          opacity: 0.7;
        `
      default:
        return `
          background-color: #f5d068;
          color: #2b1b0b;
          border: 1px solid #e2b74d;
        `
    }
  }}

  /* Punch effect on the sides (similar to a ticket) */
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    width: 12px;
    height: 12px;
    background: #f7f7f8;
    border-radius: 50%;
    transform: translateY(-50%);
  }

  &::before {
    left: -6px;
  }

  &::after {
    right: -6px;
  }

  &:hover {
    transform: translateY(-1px);
    ${p =>
      p.$variant === 'winner'
        ? 'box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4);'
        : ''}
  }

  /* Active state */
  ${p =>
    p.$active
      ? `
    border-color: ${
      p.$variant === 'winner'
        ? '#065f46'
        : p.$variant === 'loser'
        ? '#374151'
        : '#c88719'
    };
    transform: translateY(-2px);
  `
      : ''}
`

/* -----------------------------------------------------------------------------
 * Empty state - Message when there are no lucky numbers
 * -------------------------------------------------------------------------- */
export const EmptyState = styled.div`
  flex: 1;
  background: white;
  border-radius: 14px;
  padding: 24px;
  text-align: center;
  color: #64748b;
  border: 1px dashed rgba(15, 23, 42, 0.12);
  box-sizing: border-box;
`

/**
 * =============================================================================
 * TICKET MODAL STYLES
 * =============================================================================
 * Styles for the detailed view of a lucky number
 * (displayed when clicking on a number)
 */

/* -----------------------------------------------------------------------------
 * Main container of the ticket - Physical ticket design
 * -------------------------------------------------------------------------- */
export const TicketModalContainer = styled.div<{
  $variant?: 'default' | 'winner' | 'loser'
}>`
  position: relative;
  border-radius: 18px;
  padding: 22px 24px;
  border: 2px solid;
  overflow: hidden;
  transition: all 0.3s ease;

  ${p => {
    switch (p.$variant) {
      case 'winner':
        return `
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border-color: #047857;
          box-shadow: 0 18px 30px rgba(16, 185, 129, 0.35);
        `
      case 'loser':
        return `
          background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%);
          border-color: #4b5563;
          box-shadow: 0 18px 30px rgba(107, 114, 128, 0.25);
        `
      default:
        return `
          background: linear-gradient(135deg, #f7d36d 0%, #f2c14f 100%);
          border-color: #e0a93a;
          box-shadow: 0 18px 30px rgba(122, 74, 0, 0.25);
        `
    }
  }}
`

/* -----------------------------------------------------------------------------
 * Ticket body - Contains the main information
 * -------------------------------------------------------------------------- */
export const TicketBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-right: 96px;
`

export const TicketTitle = styled.div<{
  $variant?: 'default' | 'winner' | 'loser'
}>`
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: color 0.3s ease;
  opacity: 0.8;

  ${p => {
    switch (p.$variant) {
      case 'winner':
        return 'color: #ecfdf5;'
      case 'loser':
        return 'color: #f3f4f6;'
      default:
        return 'color: #6b3a00;'
    }
  }}
`

export const TicketNumber = styled.div<{
  $variant?: 'default' | 'winner' | 'loser'
}>`
  font-size: 40px;
  font-weight: 900;
  letter-spacing: 2px;
  transition: color 0.3s ease;

  ${p => {
    switch (p.$variant) {
      case 'winner':
        return 'color: white;'
      case 'loser':
        return 'color: white;'
      default:
        return 'color: #5c2f00;'
    }
  }}
`

export const TicketMeta = styled.div<{
  $variant?: 'default' | 'winner' | 'loser'
}>`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  font-size: 12px;
  font-weight: 600;
  transition: color 0.3s ease;

  ${p => {
    switch (p.$variant) {
      case 'winner':
        return 'color: #d1fae5;'
      case 'loser':
        return 'color: #e5e7eb;'
      default:
        return 'color: #6b3a00;'
    }
  }}
`

/* -----------------------------------------------------------------------------
 * Punch and stub sides - Visual details of the ticket
 * -------------------------------------------------------------------------- */
export const TicketPerforation = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 86px;
  width: 0;
  border-right: 2px dashed rgba(124, 70, 0, 0.35);
`

export const TicketStubs = styled.div<{
  $variant?: 'default' | 'winner' | 'loser'
}>`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: 86px;
  border-left: 2px solid;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  ${p => {
    switch (p.$variant) {
      case 'winner':
        return `
          background: linear-gradient(180deg, #34d399 0%, #10b981 100%);
          border-left-color: #059669;
        `
      case 'loser':
        return `
          background: linear-gradient(180deg, #d1d5db 0%, #9ca3af 100%);
          border-left-color: #6b7280;
        `
      default:
        return `
          background: linear-gradient(180deg, #f8d772 0%, #efb945 100%);
          border-left-color: #e0a93a;
        `
    }
  }}
`

export const TicketStubText = styled.div<{
  $variant?: 'default' | 'winner' | 'loser'
}>`
  writing-mode: vertical-rl;
  text-orientation: mixed;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 1px;
  transition: color 0.3s ease;

  ${p => {
    switch (p.$variant) {
      case 'winner':
        return 'color: #064e3b;'
      case 'loser':
        return 'color: #374151;'
      default:
        return 'color: #6f3f00;'
    }
  }}
`

/* -----------------------------------------------------------------------------
 * Decorative holes of the ticket (top and bottom)
 * -------------------------------------------------------------------------- */
export const TicketHole = styled.span`
  position: absolute;
  width: 16px;
  height: 16px;
  background: #fff;
  border-radius: 50%;
  z-index: 999;
`

export const TicketHoleTop = styled(TicketHole)`
  top: 16px;
  left: -8px;
`

export const TicketHoleBottom = styled(TicketHole)`
  bottom: 16px;
  left: -8px;
`

/* =============================================================================
 * PRIZE INFORMATION IN THE TICKET MODAL
 * =============================================================================
 * Section that displays the prize details when the number is a winner
 */

export const PrizeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  margin-top: 12px;
  backdrop-filter: blur(8px);
  border: 2px solid rgba(255, 255, 255, 0.25);
`

export const PrizeSectionTitle = styled.div`
  font-size: 14px;
  font-weight: 800;
  color: white;
  text-transform: uppercase;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  gap: 8px;
`

export const PrizeBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  color: white;
  backdrop-filter: blur(4px);
  margin-top: 4px;
`

/* -----------------------------------------------------------------------------
 * Winner prize card - Displayed inside the PrizeSection
 * -------------------------------------------------------------------------- */
export const WinnerPrizeCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`

export const PrizeImageWrapper = styled.div`
  position: relative;
  width: 70px;
  height: 70px;
  border-radius: 10px;
  overflow: hidden;
  background: #f3f4f6;
  border: 2px solid #e5e7eb;
  flex-shrink: 0;
`

export const PrizeImageInModal = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

export const PrizeDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
`

export const PrizeNameInModal = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.3;
`

export const PrizeDescriptionInModal = styled.p`
  margin: 0;
  font-size: 13px;
  color: #64748b;
  line-height: 1.4;
`

export const PrizeValueInModal = styled.div`
  font-size: 15px;
  font-weight: 800;
  color: #059669;
  display: flex;
  align-items: center;
  gap: 4px;
`

/* -----------------------------------------------------------------------------
 * Winner banner - Special highlight for winner numbers
 * -------------------------------------------------------------------------- */
export const PrizeWinnerBanner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  margin-top: 16px;
  border: 3px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
`

export const PrizeWinnerTitle = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: #059669;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  display: flex;
  align-items: center;
  gap: 6px;
`

export const PrizeWinnerName = styled.div`
  font-size: 24px;
  font-weight: 900;
  color: #0f172a;
  line-height: 1.2;
  text-align: left;
`

export const PrizeWinnerValue = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: #059669;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
`
